import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FileText, CheckCircle, DollarSign, Award, Mail } from 'lucide-react';
import { useMonthlyUpsell } from './MonthlyUpsellContext';
export function ApplicationAndPricingSection() {
  const {
    showUpsell
  } = useMonthlyUpsell();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-138">
      {/* Mystical background effects - matching reference snippet */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-139" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl animate-pulse-slow" data-id="element-140" />
      <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-141" />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto" data-id="element-142">
        {/* APPLICATION QUESTIONS SECTION */}
        <div className="relative mb-16 sm:mb-20" data-id="element-143">
          <div className="absolute inset-0 bg-gradient-radial from-amber-600/20 via-transparent to-transparent blur-3xl" data-id="element-144" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-10 md:p-12 rounded-lg shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-145">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12" data-id="element-146">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-147" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-148" />
            </div>
            <div className="absolute top-0 right-0 w-12 h-12" data-id="element-149">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-150" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-151" />
            </div>
            <div className="absolute bottom-0 left-0 w-12 h-12" data-id="element-152">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-153" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-154" />
            </div>
            <div className="absolute bottom-0 right-0 w-12 h-12" data-id="element-155">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-156" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-157" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-8" data-id="element-158">
              <div className="relative" data-id="element-159">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-160" />
                <FileText className="relative w-16 h-16 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-161" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8" data-id="element-162">
              <h3 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-amber-400 mb-4" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
            }} data-id="element-163">
                When You Go to Join, You'll Get An Application Form With
                Questions Like:
              </h3>
            </div>

            {/* Questions */}
            <div className="space-y-6 text-base sm:text-lg text-stone-300 mb-12" data-id="element-164">
              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-4 sm:p-6 rounded-r-lg" data-id="element-165">
                <p className="flex items-start gap-3" data-id="element-166">
                  <span className="text-amber-400 flex-shrink-0 text-lg sm:text-xl" data-id="element-167">
                    →
                  </span>
                  <span className="text-stone-100" data-id="element-168">
                    "The traditional medical system is broken and the
                    'biohacking' scene is mostly noise. Have you experienced
                    this? If so, how?"
                  </span>
                </p>
              </div>

              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-4 sm:p-6 rounded-r-lg" data-id="element-169">
                <p className="flex items-start gap-3" data-id="element-170">
                  <span className="text-amber-400 flex-shrink-0 text-lg sm:text-xl" data-id="element-171">
                    →
                  </span>
                  <span className="text-stone-100" data-id="element-172">
                    "What protocols, treatments, or 'biohacks' have you tried
                    that failed to deliver what was promised? Why do you believe
                    those attempts failed?"
                  </span>
                </p>
              </div>

              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-4 sm:p-6 rounded-r-lg" data-id="element-173">
                <p className="flex items-start gap-3" data-id="element-174">
                  <span className="text-amber-400 flex-shrink-0 text-lg sm:text-xl" data-id="element-175">
                    →
                  </span>
                  <span className="text-stone-100" data-id="element-176">
                    "We often challenge cherished beliefs with scientific data.
                    Are you prepared to accept information that may contradict
                    what 'influencers' or other experts may have told you?"
                  </span>
                </p>
              </div>

              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-4 sm:p-6 rounded-r-lg" data-id="element-177">
                <p className="flex items-start gap-3" data-id="element-178">
                  <span className="text-amber-400 flex-shrink-0 text-lg sm:text-xl" data-id="element-179">
                    →
                  </span>
                  <span className="text-stone-100" data-id="element-180">
                    "If we look back 12 months from now and regret not accepting
                    you, what would we have missed out on?"
                  </span>
                </p>
              </div>

              <div className="text-center space-y-4 sm:space-y-6 mt-10 sm:mt-12" data-id="element-181">
                <p className="text-xl sm:text-2xl font-bold text-stone-100" data-id="element-182">
                  These aren't just questions.
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-amber-400" style={{
                textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
              }} data-id="element-183">
                  They're a filter.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 sm:p-8 mt-8 rounded-lg" data-id="element-184">
                <p className="text-lg sm:text-xl font-bold text-purple-400 mb-4 sm:mb-6" data-id="element-185">
                  We're looking for people who:
                </p>
                <div className="space-y-3 text-sm sm:text-base" data-id="element-186">
                  <p className="flex items-start gap-3" data-id="element-187">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-188" />
                    <span className="text-stone-100" data-id="element-189">
                      Have been burned by bad information before
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-190">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-191" />
                    <span className="text-stone-100" data-id="element-192">
                      Are ready to be challenged
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-193">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-194" />
                    <span className="text-stone-100" data-id="element-195">
                      Are committed to mastery, not shortcuts
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-196">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-197" />
                    <span className="text-stone-100" data-id="element-198">
                      Understand this is a PRIVILEGE, not a commodity
                    </span>
                  </p>
                </div>
              </div>

              <div className="text-center space-y-4 mt-8" data-id="element-199">
                <p className="text-lg sm:text-xl text-stone-100" data-id="element-200">
                  If answering these questions makes you uncomfortable...
                </p>
                <p className="text-xl sm:text-2xl font-bold text-red-400" data-id="element-201">
                  You do not qualify.
                </p>
                <p className="text-lg sm:text-xl text-stone-100 mt-6" data-id="element-202">
                  But if they make you EXCITED...
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-green-400" style={{
                textShadow: '0 0 30px rgba(74, 222, 128, 0.5)'
              }} data-id="element-203">
                  You're exactly who we're looking for.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* VALUE BREAKDOWN SECTION */}
        <div className="relative mb-16 sm:mb-20" data-id="element-204">
          <div className="absolute inset-0 bg-gradient-radial from-blue-600/20 via-transparent to-transparent blur-3xl" data-id="element-205" />

          <div className="relative bg-gradient-to-br from-blue-950/60 via-stone-950/80 to-black border-4 border-blue-600/60 p-6 sm:p-10 md:p-12 rounded-lg shadow-[0_0_60px_rgba(59,130,246,0.5)]" data-id="element-206">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12" data-id="element-207">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-transparent" data-id="element-208" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-transparent" data-id="element-209" />
            </div>
            <div className="absolute top-0 right-0 w-12 h-12" data-id="element-210">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-blue-500 to-transparent" data-id="element-211" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-500 to-transparent" data-id="element-212" />
            </div>
            <div className="absolute bottom-0 left-0 w-12 h-12" data-id="element-213">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-transparent" data-id="element-214" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-blue-500 to-transparent" data-id="element-215" />
            </div>
            <div className="absolute bottom-0 right-0 w-12 h-12" data-id="element-216">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-blue-500 to-transparent" data-id="element-217" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-blue-500 to-transparent" data-id="element-218" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-8" data-id="element-219">
              <div className="relative" data-id="element-220">
                <div className="absolute inset-0 bg-blue-500/40 blur-xl animate-pulse-slow" data-id="element-221" />
                <DollarSign className="relative w-16 h-16 text-blue-400 drop-shadow-[0_0_15px_rgba(59,130,246,0.8)]" data-id="element-222" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-8" data-id="element-223">
              <h3 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-blue-400 mb-4" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(59, 130, 246, 0.6)'
            }} data-id="element-224">
                The Real Value (If You Were Trying To Get This Any Other Way)
              </h3>
            </div>

            {/* Value breakdown */}
            <div className="space-y-6 text-base sm:text-lg text-stone-300 mb-12" data-id="element-225">
              <p className="text-lg sm:text-xl text-center text-stone-100 font-semibold" data-id="element-226">
                Let's be brutally clear about what this is actually worth.
              </p>

              <div className="text-center space-y-2 mb-8" data-id="element-227">
                <p className="text-stone-100" data-id="element-228">Not in theoretical value.</p>
                <p className="text-lg sm:text-xl font-bold text-blue-400" data-id="element-229">
                  In real-world market rates for this level of expertise.
                </p>
              </div>

              {/* Value items */}
              <div className="space-y-4 sm:space-y-6" data-id="element-230">
                <div className="bg-blue-950/30 border-l-4 border-blue-500 p-4 sm:p-6 rounded-r-lg" data-id="element-231">
                  <p className="text-lg sm:text-xl font-bold text-blue-400 mb-2" data-id="element-232">
                    PEPTIDE MASTERY 2.0 ALONE: $2,500
                  </p>
                </div>

                <div className="bg-blue-950/30 border-l-4 border-blue-500 p-4 sm:p-6 rounded-r-lg" data-id="element-233">
                  <p className="text-lg sm:text-xl font-bold text-blue-400 mb-2 sm:mb-3" data-id="element-234">
                    12 MONTHS OF LIVE ACCESS TO BRENDEN & ALI: $18,000+
                  </p>
                  <p className="text-stone-300 text-xs sm:text-sm" data-id="element-235">
                    (Based on $1,500/hour private consultation rate × 36+ hours
                    of live sessions. We're being CONSERVATIVE here and valuing
                    it at just ONE THIRD of their normal rate since it's not
                    private 1-on-1 time. But the real value is far higher
                    because this is RETAINER access, not one-off consultations.)
                  </p>
                </div>

                <div className="bg-blue-950/30 border-l-4 border-blue-500 p-4 sm:p-6 rounded-r-lg" data-id="element-236">
                  <p className="text-lg sm:text-xl font-bold text-blue-400 mb-2 sm:mb-3" data-id="element-237">
                    EXCLUSIVE BONUSES, CASE STUDIES, PURITY REPORTS: $5,000+
                  </p>
                  <p className="text-stone-300 text-xs sm:text-sm" data-id="element-238">
                    (Monthly themed intelligence, scientific archives,
                    membership discounts, guest advisors, case study breakdowns)
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500 p-6 sm:p-8 mt-8 rounded-lg text-center" data-id="element-239">
                <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-2 sm:mb-4" style={{
                textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
              }} data-id="element-240">
                  TOTAL ACTUAL VALUE: $25,500+
                </p>
              </div>

              <div className="mt-10 sm:mt-12" data-id="element-241">
                <p className="text-lg sm:text-xl font-bold text-stone-100 mb-4 sm:mb-6" data-id="element-242">
                  If you tried to get this level of access any other way, you'd
                  need:
                </p>

                <div className="space-y-3 sm:space-y-4 pl-4 sm:pl-6 border-l-2 border-blue-500/30 text-sm sm:text-base" data-id="element-243">
                  <p className="flex items-start gap-3" data-id="element-244">
                    <span className="text-blue-400 flex-shrink-0" data-id="element-245">→</span>
                    <span className="text-stone-100" data-id="element-246">
                      $15,000 to $120,000 for a private transformation program
                      with Brenden and Ali
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-247">
                    <span className="text-blue-400 flex-shrink-0" data-id="element-248">→</span>
                    <span className="text-stone-100" data-id="element-249">
                      OR $1,500 per session × however many sessions you need
                    </span>
                  </p>
                  <p className="flex items-start gap-3" data-id="element-250">
                    <span className="text-blue-400 flex-shrink-0" data-id="element-251">→</span>
                    <span className="text-stone-100" data-id="element-252">
                      OR $25,000+ for elite-tier strategic retainer (by
                      invitation only)
                    </span>
                  </p>
                </div>

                <p className="text-lg sm:text-xl text-center text-stone-100 font-bold mt-6 sm:mt-8" data-id="element-253">
                  None of which are accessible to most people.
                </p>

                <p className="text-xl sm:text-2xl text-center text-blue-400 font-bold mt-3 sm:mt-4" data-id="element-254">
                  Which is why this back door is so unprecedented.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* INVESTMENT/PRICING SECTION */}
        <div className="relative" data-id="element-255">
          <div className="absolute inset-0 bg-gradient-radial from-purple-600/20 via-transparent to-transparent blur-3xl" data-id="element-256" />

          <div className="relative bg-gradient-to-br from-purple-950/60 via-stone-950/80 to-black border-4 border-purple-600/60 p-6 sm:p-10 md:p-12 rounded-lg shadow-[0_0_60px_rgba(168,85,247,0.5)]" data-id="element-257">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12" data-id="element-258">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-transparent" data-id="element-259" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-purple-500 to-transparent" data-id="element-260" />
            </div>
            <div className="absolute top-0 right-0 w-12 h-12" data-id="element-261">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-purple-500 to-transparent" data-id="element-262" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-purple-500 to-transparent" data-id="element-263" />
            </div>
            <div className="absolute bottom-0 left-0 w-12 h-12" data-id="element-264">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-transparent" data-id="element-265" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-purple-500 to-transparent" data-id="element-266" />
            </div>
            <div className="absolute bottom-0 right-0 w-12 h-12" data-id="element-267">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-purple-500 to-transparent" data-id="element-268" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-purple-500 to-transparent" data-id="element-269" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-8" data-id="element-270">
              <div className="relative" data-id="element-271">
                <div className="absolute inset-0 bg-purple-500/40 blur-xl animate-pulse-slow" data-id="element-272" />
                <Award className="relative w-16 h-16 text-purple-400 drop-shadow-[0_0_15px_rgba(168,85,247,0.8)]" data-id="element-273" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-10 sm:mb-12" data-id="element-274">
              <h3 data-investment-section className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-purple-400 mb-4 sm:mb-6" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(168, 85, 247, 0.6)',
              scrollMarginTop: '80px'
            }} data-id="element-275">
                The Investment
              </h3>

              <div className="space-y-3 sm:space-y-4 max-w-3xl mx-auto" data-id="element-276">
                <p className="text-lg sm:text-xl text-stone-100" data-id="element-277">
                  Now here's the part that might surprise you.
                </p>
                <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-278">
                  We're not charging anywhere CLOSE to what this is worth.
                </p>
                <p className="text-lg sm:text-xl text-stone-100" data-id="element-279">In fact...</p>
                <p className="text-xl sm:text-2xl text-amber-400 font-bold" style={{
                textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
              }} data-id="element-280">
                  We're pricing this so LOW that you're basically getting
                  Peptide Mastery 2.0 for FREE...
                </p>
                <p className="text-lg sm:text-xl text-stone-100" data-id="element-281">
                  And getting ANOTHER DISCOUNT for the 12 months of live access
                  to Brenden and Ali.
                </p>
                <p className="text-xl sm:text-2xl text-purple-400 font-bold mt-6 sm:mt-8" data-id="element-282">
                  Here are your options:
                </p>
              </div>
            </div>

            {/* OPTION 1: PAY IN FULL */}
            <div className="mb-10 sm:mb-12" data-id="element-283">
              <div className="relative bg-gradient-to-br from-green-950/40 to-black border-4 border-green-500/60 p-6 sm:p-8 rounded-lg shadow-[0_0_40px_rgba(34,197,94,0.4)]" data-id="element-284">
                {/* Best Value Badge */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-285">
                  <div className="px-4 sm:px-6 py-1.5 sm:py-2 bg-gradient-to-r from-green-600 to-green-500 rounded-full text-white text-xs sm:text-sm font-bold shadow-lg whitespace-nowrap" data-id="element-286">
                    BEST VALUE
                  </div>
                </div>

                <div className="text-center mb-6 mt-4" data-id="element-287">
                  <h4 className="text-2xl sm:text-3xl font-bold text-green-400 mb-3 sm:mb-4" data-id="element-288">
                    OPTION 1: BEST VALUE — UNLOCK EVERYTHING IMMEDIATELY
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-289">
                  <p className="text-stone-100" data-id="element-290">
                    When you combine everything together you're getting tens of
                    thousands of dollars of value.
                  </p>

                  <p className="text-stone-100" data-id="element-291">
                    If you are accepted into our Private Research Order and you
                    receive the complete Peptide Mastery 2.0 training, we would
                    normally price both at $5,500.
                  </p>

                  <p className="text-lg sm:text-xl font-bold text-green-400" data-id="element-292">
                    As a PRO MEMBER, we're offering you the most insane package
                    possible.
                  </p>

                  <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500 p-6 sm:p-8 my-6 text-center" data-id="element-293">
                    <p className="text-4xl sm:text-5xl font-bold text-amber-400 mb-2" style={{
                    textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
                  }} data-id="element-294">
                      Only $2,490
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100" data-id="element-295">
                      for everything.
                    </p>
                  </div>

                  <div className="space-y-3 mb-6 text-sm sm:text-base" data-id="element-296">
                    <p className="flex items-start gap-3" data-id="element-297">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-298" />
                      <span className="text-stone-100" data-id="element-299">
                        Full Peptide Mastery 2.0 unlocked immediately
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-300">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-301" />
                      <span className="text-stone-100" data-id="element-302">
                        12 months of PSI PRO membership
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-303">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-304" />
                      <span className="text-stone-100" data-id="element-305">
                        All live sessions, bonuses, case studies, purity reports
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-306">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-307" />
                      <span className="text-stone-100" data-id="element-308">
                        You get to KEEP your discounted rate forever (as long as
                        you remain a member)
                      </span>
                    </p>
                  </div>

                  <div className="bg-green-950/30 border-l-4 border-green-500 p-4 sm:p-6 my-6" data-id="element-309">
                    <p className="text-stone-100 mb-2 text-sm sm:text-base" data-id="element-310">
                      This is for people who already understand and trust the
                      level of dedication, value and passion we put into our
                      work, and want to benefit more by going deeper with us.
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-green-400 mt-4" data-id="element-311">
                      You're paying LESS than Peptide Mastery alone normally
                      costs ($2,500)...
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-green-400" data-id="element-312">
                      And getting 12 months of direct access to the masters
                      ($18,000+ value) included.
                    </p>
                  </div>

                  <a href="https://go.peptidescienceinstitute.com/pm-pro-annual-fm" className="block w-full px-6 py-4 sm:py-5 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white text-center font-bold text-base sm:text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(34,197,94,0.4)] hover:shadow-[0_0_50px_rgba(34,197,94,0.6)] hover:scale-105 min-h-[44px]" data-id="element-313">
                    JOIN PRO NOW — PAY IN FULL
                  </a>
                </div>
              </div>
            </div>

            {/* OPTION 2: MONTHLY PAYMENTS */}
            <div className="mb-10 sm:mb-12" data-id="element-314">
              <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/60 p-6 sm:p-8 rounded-lg" data-id="element-315">
                <div className="text-center mb-6" data-id="element-316">
                  <h4 className="text-2xl sm:text-3xl font-bold text-blue-400 mb-3 sm:mb-4" data-id="element-317">
                    OPTION 2: MONTHLY PAYMENTS
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-318">
                  <p className="text-stone-100" data-id="element-319">
                    Now for people who understand and trust our level of
                    dedication, value and passion we put into our work, and want
                    to benefit more by going deeper with us… But prefer a lower
                    monthly payment.
                  </p>

                  <p className="text-stone-100" data-id="element-320">
                    You can still get the complete Peptide Mastery 2.0 course
                    (worth $2,500) AND access to PSI PRO for 12 months of direct
                    access to the masters (a complete package which would be
                    $5,500 all together).
                  </p>

                  <p className="text-lg sm:text-xl font-bold text-blue-400" data-id="element-321">
                    As a PRO MEMBER who prefers paying monthly, we're also
                    offering you the most insane package possible.
                  </p>

                  <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500 p-6 sm:p-8 my-6 text-center" data-id="element-322">
                    <p className="text-4xl sm:text-5xl font-bold text-amber-400 mb-2" style={{
                    textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
                  }} data-id="element-323">
                      Only $249 Today
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100" data-id="element-324">
                      Then 11 monthly payments of $249
                    </p>
                  </div>

                  <div className="space-y-3 mb-6 text-sm sm:text-base" data-id="element-325">
                    <p className="flex items-start gap-3" data-id="element-326">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-327" />
                      <span className="text-stone-100" data-id="element-328">
                        Peptide Mastery 2.0 unlocked monthly (aligned with your
                        learning journey)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-329">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-330" />
                      <span className="text-stone-100" data-id="element-331">
                        12 months of PSI PRO membership
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-332">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-333" />
                      <span className="text-stone-100" data-id="element-334">
                        All live sessions, bonuses, case studies, purity reports
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-335">
                      <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 flex-shrink-0 mt-0.5 sm:mt-1" data-id="element-336" />
                      <span className="text-stone-100" data-id="element-337">
                        Then renew at the same $249 rate forever (as long as you
                        stay)
                      </span>
                    </p>
                  </div>

                  <div className="bg-blue-950/30 border-l-4 border-blue-500 p-4 sm:p-6 my-6" data-id="element-338">
                    <p className="text-stone-100 text-sm sm:text-base" data-id="element-339">
                      Normal membership price ALONE (without Peptide Mastery)
                      will be $250/mo and WITH Peptide Mastery we may eventually
                      take to $500 per month.
                    </p>
                  </div>

                  <button onClick={showUpsell} className="block w-full px-6 py-4 sm:py-5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-center font-bold text-base sm:text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(59,130,246,0.4)] hover:shadow-[0_0_50px_rgba(59,130,246,0.6)] hover:scale-105 min-h-[44px] cursor-pointer" data-id="element-340">
                    JOIN PRO NOW — MONTHLY
                  </button>
                </div>
              </div>
            </div>

            {/* OPTION 3: OG PEPTIDE MASTERY OWNERS */}
            <div data-id="element-341">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/60 p-6 sm:p-8 rounded-lg" data-id="element-342">
                <div className="text-center mb-6" data-id="element-343">
                  <h4 className="text-2xl sm:text-3xl font-bold text-purple-400 mb-3 sm:mb-4" data-id="element-344">
                    OPTION 3: FOR OG PEPTIDE MASTERY OWNERS (EXCLUSIVE)
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-345">
                  <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-346">
                    Already own Peptide Mastery?
                  </p>

                  <p className="text-stone-100" data-id="element-347">
                    You already have the blueprints.
                  </p>

                  <p className="text-lg sm:text-xl font-bold text-purple-400" data-id="element-348">
                    Now join the active command center.
                  </p>

                  <div className="bg-purple-950/30 border-l-4 border-purple-500 p-4 sm:p-6 my-6" data-id="element-349">
                    <p className="text-stone-100 mb-3 sm:mb-4 text-sm sm:text-base" data-id="element-350">
                      Contact us at{' '}
                      <span className="font-bold text-purple-400 break-all" data-id="element-351">
                        support@support.peptidescienceinstitute.com
                      </span>{' '}
                      and let us know you're a Peptide Mastery owner.
                    </p>
                    <p className="text-stone-100 text-sm sm:text-base" data-id="element-352">
                      We will send you the special "Peptide Mastery owner
                      discount" offer privately.
                    </p>
                  </div>

                  <a href="mailto:support@support.peptidescienceinstitute.com" className="flex items-center justify-center gap-2 sm:gap-3 w-full px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white text-center font-bold text-base sm:text-lg rounded-lg transition-all duration-300 shadow-[0_0_30px_rgba(168,85,247,0.4)] hover:shadow-[0_0_50px_rgba(168,85,247,0.6)] hover:scale-105 min-h-[44px]" data-id="element-353">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" data-id="element-354" />
                    <span data-id="element-355">CONTACT US FOR ALUMNI PRICING</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}